import * as React from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Company, CompleteUser, Permissions } from 'types';
import Targets from 'utils/targets';
import { useHistory } from 'react-router-dom';
import * as LocalStorage from '../local-storage';
import { NotificationManager } from 'react-notifications';
import i18next from 'i18next';
import PopUp from 'components/PopUp';
import ReversibleButton from 'components/reversible-button';
import SubscriptionExpiredPopup from 'components/subscription-expired-popup';

interface Props {
  initialToken: string;
  initialRefreshToken: string;
  target: Targets;
  children: any;
}

type Context = {
  token: string | null;
  setToken: (token: string | null) => void;
  refreshToken: string | null;
  setRefreshToken: (token: string | null) => void;
  user: CompleteUser | null;
  setUser: (user: CompleteUser | null) => void;
  // Optional flag allows callers to force a fresh fetch from backend (bypass cache)
  updateUser: (useFresh?: boolean) => Promise<any> | null;
  target: Targets;
};

const AppContext = React.createContext<Context>({
  token: '',
  setToken: t => t,
  refreshToken: '',
  setRefreshToken: t => t,
  user: null,
  setUser: u => u,
  updateUser: () => null,
  target: Targets.Security,
});
const { useState, useContext } = React;

/**
 * Custom hooks for contexts
 */

export const useSetToken = () => {
  const { setToken } = useContext<Context>(AppContext);
  return setToken;
};

export const useToken = () => {
  const { token } = useContext(AppContext);
  return token;
};

export const useRefreshToken = () => {
  const { refreshToken } = useContext(AppContext);
  return refreshToken;
};

export const useSetRefreshToken = () => {
  const { setRefreshToken } = useContext(AppContext);
  return setRefreshToken;
};

export const useSetUser = () => {
  const { setUser } = useContext(AppContext);
  return setUser;
};

export const useUser = () => {
  const { user } = useContext(AppContext);
  return user;
};

export const useUpdateUser = () => {
  const { updateUser } = useContext(AppContext);
  return updateUser;
};

export const usePermissions = (): Permissions | null => {
  const { user } = useContext(AppContext);

  return user && user.employments?.length > 0
    ? user.employments[0].permissions || {}
    : {};
};

export const useCompany = (): Company | null => {
  const { user } = useContext(AppContext);

  return user?.company || null;
};

export const useTarget = (): Targets => {
  const { target } = useContext(AppContext);

  return target;
};

let refreshing = false;

export const AppProvider = ({
  initialToken,
  initialRefreshToken,
  target,
  children,
}: Props) => {
  const [token, setToken] = useState<string | null>(initialToken);
  const [refreshToken, setRefreshToken] = useState<string | null>(
    initialRefreshToken
  );
  const [user, setUser] = useState<CompleteUser | null>(null);
  const history = useHistory();

  // React.useEffect(() => {
  //   // if (
  //   //   user?.company &&
  //   //   user?.company?.product_type?.toLowerCase() !== target
  //   // ) {
  //   //   history.push(`/security/app`);
  //   // }
  // }, [history, target, user]);

  // Custom setUser that also updates localStorage
  const setUserWithStorage = (newUser: CompleteUser | null) => {
    if (newUser) {
      localStorage.setItem('user', JSON.stringify(newUser));
    } else {
      localStorage.removeItem('user');
    }
    setUser(newUser);
  };

  const fetchUser = async (accessToken: string, useFresh = false) => {
    try {
      const endpoint = useFresh ? '/me/fresh' : '/me';
      const { data } = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const isAdmin = Boolean(data.user.privilegeLevel === 'ADMIN' || data.user.privilegeLevel === 'OWNER')
      const userData = {
        ...data.user,
        isAdmin: isAdmin,
        prefrences: data.user.prefrences,
        levAccess: true,
        company: data.company,
        isOwner: data.user.isOwner,
        accessLevel: data.user.privilegeLevel,
        privacy_enabled: data.team.allowsPrivacy,
        team_id: data.team.uuid,
        team_name: data.team.name,
        passwordSetByAdmin: data.user.passwordSetByAdmin || false,
        needsMfaSetup: data.user.needsMfaSetup || false, // Explicitly include needsMfaSetup flag
      };
      setUserWithStorage(userData);
    } catch (err: any) {
      if (err.response?.status === 401 && err.response?.data?.message === 'Subscriptions expired') {
        // Get user data from localStorage
        const storedUser = localStorage.getItem('user');
        let isAdmin = false;
        
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            isAdmin = Boolean(parsedUser.accessLevel === 'ADMIN' || parsedUser.accessLevel === 'OWNER');
          } catch (e) {
            console.error('Error parsing stored user data:', e);
          }
        }

        // Show subscription expired popup
        const root = document.createElement('div');
        root.id = 'subscription-expired-popup-root';
        document.body.appendChild(root);
        
        const cleanup = () => {
          ReactDOM.unmountComponentAtNode(root);
          root.remove();
        };

        ReactDOM.render(
          <SubscriptionExpiredPopup
            isVisible={true}
            isAdmin={isAdmin}
            onLogout={() => {
              setToken(null);
              setRefreshToken(null);
            }}
            onDismiss={cleanup}
          />,
          root
        );
        
        return;
      }
      throw err;
    }
  };

  const refresh = React.useCallback(async () => {
    try {
      const { data: refreshTokenData } = await axios.post('/auth/refresh', {
        refresh: refreshToken,
      });
      setToken(refreshTokenData.access);
    } catch {
      setRefreshToken(null);
      setToken(null);
    }
  }, [refreshToken, setToken, setRefreshToken]);

  const updateUser = React.useCallback(async (useFresh = false) => {
    if (!token) {
      return;
    }

    try {
      await fetchUser(token, useFresh);
    } catch (err: any) {
      if (err.response != null && err.response.status === 401 && !refreshing) {
        if (refreshToken) {
          refreshing = true;
          try {
            await refresh();
            refreshing = false;
          } catch (refreshErr) {
            console.error('[AppContext] Error refreshing token:', refreshErr);
            setToken(null);
            setRefreshToken(null);
            refreshing = false;
          }
        } else {
          setToken(null);
          setRefreshToken(null);
        }
      } else {
        console.error('[AppContext] Error fetching user:', err);
        if (err.response?.status === 401) {
          const storedUser = localStorage.getItem('user');
          if (!storedUser) {
            setToken(null);
            setRefreshToken(null);
          }
        }
      }
    }
  }, [token, refreshToken, refresh, setToken, setRefreshToken]);

  React.useEffect(() => {
    LocalStorage.setToken(token);
    LocalStorage.setRefreshToken(refreshToken);
    if (!token) {
      setUserWithStorage(null);
    } else {
      updateUser();
    }
  }, [token, refreshToken, setUser, updateUser]);

  return (
    <AppContext.Provider
      value={{
        token,
        setToken,
        user,
        setUser: setUserWithStorage,
        updateUser,
        refreshToken,
        setRefreshToken,
        target,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
